import {
  useCreateFolderMutation,
  useGetArchivedWorkspaceFoldersQuery,
  useGetWorkspaceFoldersQuery,
  useGetWorkspacesQuery,
  useLazyGetFoldersQuery,
} from '@carboncopyco/data';
import { FolderIcon, NoteIcon } from '@carboncopyco/icons';
import {
  ColorHex,
  InputText,
  Loading,
  TextBase,
  TooltipBubble,
  notify,
} from '@carboncopyco/ui';
import { useFolderAnalytics } from '@carboncopyco/utils';
import { useBoolean, useUpdateEffect } from 'ahooks';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { string } from 'yup';
import { encodeNodeId, indents, parseNodeId } from '../BrowseTree';

const validationSchema = string().matches(
  /^[^/]*$/,
  'Forward-slashed are forbidden in folder names. Please try a different name.'
);

type AddNewFolderInputProps = {
  onClose: () => void;
  selected: string;
  setSelected: (id: string) => void;
};

export const AddNewFolderInput = ({
  onClose,
  selected,
  setSelected,
}: AddNewFolderInputProps) => {
  const { organizationId } = useParams();
  const { trackFolderClickCreate } = useFolderAnalytics('folder_tree');

  const [createNewFolder, { isUninitialized, isLoading: isCreatingNewFolder }] =
    useCreateFolderMutation();
  const { data: workspaceFolders } = useGetWorkspaceFoldersQuery({
    organizationId,
    workspaceId: parseNodeId(selected)[3],
    limit: 50,
  });
  const { data: archivedWorkspaceFolders } =
    useGetArchivedWorkspaceFoldersQuery({
      organizationId,
      workspaceId: parseNodeId(selected)[3],
      limit: 50,
    });
  const [getFolders] = useLazyGetFoldersQuery();
  const { data: workspaces } = useGetWorkspacesQuery({
    organizationId,
  });
  const [newFolderName, setNewFolderName] = useState<string>();
  const [error, setError] = useState<string>();
  const [isValidating, setIsValidating] = useBoolean();
  const anchorRef = useRef(null);
  const errorTooltipAnchorRef = useRef(null);

  useUpdateEffect(() => {
    if (!workspaceFolders || !archivedWorkspaceFolders) {
      return;
    }

    setIsValidating.setTrue();

    const nameExists = !![
      ...workspaceFolders,
      ...archivedWorkspaceFolders,
    ].filter((folder) => newFolderName === folder.name).length;

    if (nameExists) {
      setError('This name is already taken. Please select a different name.');
      return;
    }

    validationSchema
      .validate(newFolderName)
      .then(() => {
        setError(undefined);
      })
      .catch((reject) => {
        setError(reject.errors[0]);
      })
      .finally(() => {
        setIsValidating.setFalse();
      });
  }, [newFolderName, workspaceFolders, archivedWorkspaceFolders]);

  const handleSubmit = () => {
    if (isCreatingNewFolder || isValidating || error) {
      return;
    }

    if (!newFolderName) {
      onClose();
      return;
    }
    const selectedNew = parseNodeId(selected);
    const selectedWorkspace = workspaces.find(
      (ws) => String(ws.id) === selectedNew[3]
    );
    trackFolderClickCreate({
      workspaceId: selectedWorkspace?.id,
      workspaceName: selectedWorkspace?.workspaceName,
      workspaceType: selectedWorkspace?.workspaceType,
    });
    createNewFolder({
      organizationId,
      workspaceId: selectedNew[3],
      name: newFolderName,
    })
      .unwrap()
      .then((newFolder) => {
        setSelected(encodeNodeId(newFolder.id, 'folder', 1, selectedNew[3]));
        getFolders({
          organizationId,
          includeShared: false,
        })
          .unwrap()
          .then(() => {
            onClose();
          });
      })
      .catch(() => {
        notify.error('Failed creating a new folder in the selected workspace');
        onClose();
      });
  };

  return isUninitialized ? (
    <div
      className={classNames(
        'flex items-center gap-3 pr-3 py-[5px] ml-6',
        indents[1]
      )}
    >
      <>
        <div className="shrink-0">
          <FolderIcon color={ColorHex.natural500} />
        </div>
        <InputText
          defaultValue={newFolderName}
          onChange={(event) => {
            setNewFolderName(event.target.value);
          }}
          state={error ? 'error' : 'normal'}
          rightElement={
            error ? (
              <>
                <TooltipBubble
                  anchorRef={errorTooltipAnchorRef}
                  title={error}
                  placement="top-end"
                />
                <div ref={errorTooltipAnchorRef}>
                  <NoteIcon color={ColorHex.danger300} />
                </div>
              </>
            ) : undefined
          }
          autoFocus
          fullWidth
          onKeyPress={(keypress) => {
            if (keypress.key === 'Enter') {
              handleSubmit();
            }
          }}
          onBlur={() => {
            handleSubmit();
          }}
        />
      </>
    </div>
  ) : (
    <div
      className={classNames(
        'flex items-center gap-3 justify-between py-2 pr-2 ml-6',
        indents[1]
      )}
    >
      <div className="flex items-center gap-3 truncate">
        <div className="shrink-0">
          <FolderIcon color={ColorHex.natural500} />
        </div>
        <TooltipBubble
          anchorRef={anchorRef}
          title={newFolderName}
          placement="top-end"
          delay={[1000, 0]}
        />
        <div
          ref={anchorRef}
          className="truncate text-ds-natural-600"
        >
          <TextBase
            color="text-ds-natural-600"
            label={newFolderName}
          />
        </div>
      </div>
      <div className="shrink-0 pr-3">
        <Loading color="text-ds-primary-500" />
      </div>
    </div>
  );
};
